import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Card,
  Tag,
  Alert,
  Popconfirm,
  Tooltip,
  Typography,
  Switch,
  Divider,
  InputNumber,
  DatePicker,
  Checkbox,
} from "antd";
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  Settings,
  Search,
  Wallet,
  Coins,
  Download,
} from "lucide-react";
import moment from "moment";
import ErrorBoundary from "../common/ErrorBoundary";
import apiService from "../../services/api";
import { exportAccountConfigsExcel } from "../../utils/accountConfigExport";
import "./AccountConfig.css";

import { isAdmin } from "../../utils/permissions";

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

const AccountConfigList = () => {
  const { user } = useSelector((state) => state.auth);
  const isUserAdmin = isAdmin(user);
  const [configs, setConfigs] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [activeBusinessLines, setActiveBusinessLines] = useState([]);
  const [activeStrategyTypes, setActiveStrategyTypes] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBorrowModal, setShowBorrowModal] = useState(false);
  const [showBorrowListModal, setShowBorrowListModal] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [currentBorrow, setCurrentBorrow] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    keyword: "",
    exchange: "",
    trade_type: "",
    is_active: undefined,
    owner_user_id: undefined, // undefined=不过滤; 0=未分配; N=指定
    business_line_id: undefined,
    strategy_type_id: undefined,
    borrow_status: "", // ""=不过滤; "has"=有借币; "none"=无借币
  });

  const [form] = Form.useForm();
  const [borrowForm] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const businessLineOptions = useMemo(() => {
    const list = [...(activeBusinessLines || [])];
    const cur = currentConfig?.business_line;
    if (cur?.id != null && !list.some((x) => x.id === cur.id)) {
      list.unshift({
        id: cur.id,
        name: `${cur.name}（未启用）`,
        code: cur.code,
      });
    }
    return list;
  }, [activeBusinessLines, currentConfig]);

  const strategyTypeOptions = useMemo(() => {
    const list = [...(activeStrategyTypes || [])];
    const cur = currentConfig?.strategy_type;
    if (cur?.id != null && !list.some((x) => x.id === cur.id)) {
      list.unshift({
        id: cur.id,
        name: `${cur.name}（未启用）`,
        code: cur.code,
      });
    }
    return list;
  }, [activeStrategyTypes, currentConfig]);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...searchParams,
      };
      // 过滤空值（保留 0 / false，它们表达「未分配 / 禁用」语义）
      Object.keys(params).forEach((key) => {
        const v = params[key];
        if (v === "" || v === undefined || v === null) {
          delete params[key];
        }
      });

      const response = await apiService.accountConfigs.getConfigs(params);
      if (response.data?.data) {
        setConfigs(response.data.data.list || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.data.total || 0,
        }));
      }
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "加载配置失败",
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchParams]);

  const loadExchanges = async () => {
    try {
      const response = await apiService.exchanges.getActiveExchanges();
      if (response.data?.data) {
        setExchanges(response.data.data || []);
      }
    } catch (err) {
      console.error("加载交易所失败:", err);
    }
  };

  const loadActiveMasterData = async () => {
    try {
      const [blRes, stRes] = await Promise.all([
        apiService.businessLines.listActive(),
        apiService.strategyTypes.listActive(),
      ]);
      setActiveBusinessLines(blRes.data?.data || []);
      setActiveStrategyTypes(stRes.data?.data || []);
    } catch (err) {
      console.error("加载业务线/策略类型失败:", err);
    }
  };

  const loadSystemUsers = useCallback(async () => {
    if (!isUserAdmin) return;
    try {
      const response = await apiService.users.getUsers({
        page: 1,
        page_size: 100,
        status: "active",
      });
      const list = response.data?.data?.list || [];
      setSystemUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("加载用户列表失败:", err);
    }
  }, [isUserAdmin]);

  useEffect(() => {
    loadConfigs();
    loadExchanges();
    loadActiveMasterData();
  }, [loadConfigs]);

  useEffect(() => {
    loadSystemUsers();
  }, [loadSystemUsers]);

  const handleSubmit = async (values) => {
    try {
      if (currentConfig) {
        const submissionData = {
          description: values.description,
          is_active: values.is_active ?? true,
          business_line_id:
            values.business_line_id != null ? values.business_line_id : 0,
          strategy_type_id:
            values.strategy_type_id != null ? values.strategy_type_id : 0,
        };
        if (isUserAdmin) {
          submissionData.owner_user_id = values.owner_user_id ?? 0;
        }
        await apiService.accountConfigs.updateConfig(currentConfig.id, submissionData);
        message.success("配置更新成功");
        setShowModal(false);
        form.resetFields();
        setCurrentConfig(null);
        loadConfigs();
        return;
      }

      const tradeTypes = values.trade_types || [];
      if (tradeTypes.length === 0) {
        message.error("请至少选择一种交易类型（现货或合约）");
        return;
      }

      const base = {
        account: values.account,
        exchange: values.exchange,
        description: values.description || "",
        is_active: values.is_active ?? true,
        business_line_id: values.business_line_id,
        strategy_type_id: values.strategy_type_id,
        ...(isUserAdmin && values.owner_user_id != null && values.owner_user_id !== undefined
          ? { owner_user_id: values.owner_user_id }
          : {}),
      };

      const created = [];
      const skipped = [];
      for (const tt of tradeTypes) {
        try {
          await apiService.accountConfigs.createConfig({ ...base, trade_type: tt });
          created.push(tt === "spot" ? "现货" : "合约");
        } catch (err) {
          const msg =
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            "";
          if (msg.includes("已存在") || msg.includes("already")) {
            skipped.push(tt === "spot" ? "现货" : "合约");
          } else {
            throw err;
          }
        }
      }

      if (created.length > 0) {
        message.success(`已创建：${created.join("、")}`);
      }
      if (skipped.length > 0) {
        message.warning(`以下类型配置已存在，已跳过：${skipped.join("、")}`);
      }
      if (created.length === 0 && skipped.length > 0) {
        message.info("所选交易类型均已存在，未新增记录");
      }

      setShowModal(false);
      form.resetFields();
      setCurrentConfig(null);
      loadConfigs();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.accountConfigs.deleteConfig(id);
      message.success("配置删除成功");
      loadConfigs();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await apiService.accountConfigs.updateConfig(config.id, {
        is_active: !config.is_active,
      });
      message.success(`配置已${config.is_active ? "禁用" : "启用"}`);
      loadConfigs();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  // 借币配置相关
  const openBorrowModal = (config, borrow = null) => {
    setCurrentConfig(config);
    setCurrentBorrow(borrow);
    if (borrow) {
      borrowForm.setFieldsValue({
        asset: borrow.asset,
        borrow_amount: borrow.borrow_amount,
        borrow_date: borrow.borrow_date ? moment(borrow.borrow_date) : null,
        interest_rate: borrow.interest_rate,
        due_date: borrow.due_date ? moment(borrow.due_date) : null,
        status: borrow.status,
        remark: borrow.remark,
      });
    } else {
      borrowForm.resetFields();
      borrowForm.setFieldsValue({ status: "active" });
    }
    setShowBorrowModal(true);
  };

  // 借币管理弹窗：账号维度的借贷统一入口（新增 / 编辑 / 删除），
  // 避免编辑入口只藏在表格展开行里而难以发现
  const openBorrowListModal = (config) => {
    setCurrentConfig(config);
    setShowBorrowListModal(true);
  };

  // 弹窗内的借币列表取自最新的 configs，保证增删改后立即反映，而不是用打开时的快照
  const currentConfigBorrows = useMemo(() => {
    if (!currentConfig) return [];
    const fresh = configs.find((c) => c.id === currentConfig.id);
    return (fresh || currentConfig).borrow_configs || [];
  }, [configs, currentConfig]);

  const handleBorrowSubmit = async (values) => {
    try {
      // 日期为空传空字符串：编辑接口以「省略=不修改、空串=清空」区分语义
      const submissionData = {
        ...values,
        borrow_date: values.borrow_date ? values.borrow_date.format("YYYY-MM-DD") : "",
        due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : "",
      };

      if (currentBorrow) {
        await apiService.accountConfigs.updateBorrowConfig(
          currentConfig.id,
          currentBorrow.id,
          submissionData,
        );
        message.success("借币配置更新成功");
      } else {
        await apiService.accountConfigs.addBorrowConfig(
          currentConfig.id,
          submissionData,
        );
        message.success("借币配置添加成功");
      }
      setShowBorrowModal(false);
      borrowForm.resetFields();
      setCurrentBorrow(null);
      loadConfigs();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const handleDeleteBorrow = async (configId, borrowId) => {
    try {
      await apiService.accountConfigs.deleteBorrowConfig(configId, borrowId);
      message.success("借币配置删除成功");
      loadConfigs();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const openModal = (config = null) => {
    if (config) {
      setCurrentConfig(config);
      form.resetFields();
      const blId = config.business_line_id ?? config.business_line?.id;
      const stId = config.strategy_type_id ?? config.strategy_type?.id;
      form.setFieldsValue({
        account: config.account,
        exchange: config.exchange,
        trade_type: config.trade_type,
        description: config.description,
        is_active: config.is_active,
        owner_user_id: config.owner_user_id ?? undefined,
        business_line_id: blId ?? undefined,
        strategy_type_id: stId ?? undefined,
      });
    } else {
      setCurrentConfig(null);
      form.resetFields();
      form.setFieldsValue({ trade_types: ["spot"], is_active: true });
    }
    setShowModal(true);
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const openBatchModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("请先勾选需要批量更新的配置");
      return;
    }
    batchForm.resetFields();
    setShowBatchModal(true);
  };

  const handleBatchSubmit = async (values) => {
    const payload = { ids: selectedRowKeys };
    if (values.is_active !== undefined) payload.is_active = values.is_active;
    if (isUserAdmin && values.owner_user_id !== undefined) {
      payload.owner_user_id = values.owner_user_id ?? 0;
    }
    if (values.business_line_id !== undefined) {
      payload.business_line_id = values.business_line_id ?? 0;
    }
    if (values.strategy_type_id !== undefined) {
      payload.strategy_type_id = values.strategy_type_id ?? 0;
    }

    const fieldKeys = ["is_active", "owner_user_id", "business_line_id", "strategy_type_id"];
    const hasField = fieldKeys.some((k) => k in payload);
    if (!hasField) {
      message.warning("请至少选择一项需要批量更新的字段");
      return;
    }

    setBatchSubmitting(true);
    try {
      const response = await apiService.accountConfigs.batchUpdate(payload);
      const result = response.data?.data || {};
      const { total = 0, success_count = 0, failed_count = 0, failed = [] } = result;

      if (failed_count === 0) {
        message.success(`批量更新完成，共 ${total} 条全部成功`);
      } else if (success_count === 0) {
        Modal.error({
          title: "批量更新失败",
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>共 {total} 条全部失败：</div>
              <ul style={{ paddingLeft: 20, maxHeight: 240, overflow: "auto" }}>
                {failed.map((item) => (
                  <li key={item.id}>
                    ID {item.id}：{item.message}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
      } else {
        Modal.warning({
          title: "批量更新部分成功",
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>
                共 {total} 条，成功 {success_count} 条，失败 {failed_count} 条：
              </div>
              <ul style={{ paddingLeft: 20, maxHeight: 240, overflow: "auto" }}>
                {failed.map((item) => (
                  <li key={item.id}>
                    ID {item.id}：{item.message}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
      }

      setShowBatchModal(false);
      batchForm.resetFields();
      setSelectedRowKeys([]);
      loadConfigs();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "批量更新失败",
      );
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    loadConfigs();
  };

  // 导出名下全部账号配置（含借币明细）；有筛选条件时按筛选结果导出
  const handleExport = async () => {
    setExporting(true);
    try {
      const count = await exportAccountConfigsExcel(searchParams);
      if (count === 0) {
        message.warning("没有可导出的账号配置");
      } else {
        message.success(`已导出 ${count} 条账号配置`);
      }
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "导出失败",
      );
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: "账号信息",
      key: "account_info",
      width: 200,
      render: (_, record) => (
        <div className="account-info-cell">
          <div className="account-name">
            <Wallet className="account-icon" size={16} />
            <span className="account-text">{record.account}</span>
          </div>
          <Text type="secondary" className="exchange-text">
            {record.exchange} · {record.trade_type === "spot" ? "现货" : "合约"}
          </Text>
        </div>
      ),
    },
    {
      title: "所属用户",
      key: "owner",
      width: 140,
      ellipsis: true,
      render: (_, record) => {
        const isSelf =
          !isUserAdmin &&
          user?.id != null &&
          record.owner_user_id != null &&
          Number(record.owner_user_id) === Number(user.id);
        if (isSelf) {
          return (
            <Text>
              本人（{record.owner?.username ?? user?.username ?? "—"}）
            </Text>
          );
        }
        return record.owner?.username ? (
          <Text>{record.owner.username}</Text>
        ) : (
          <Text type="secondary">未指定</Text>
        );
      },
    },
    {
      title: "业务线",
      key: "business_line",
      width: 120,
      ellipsis: true,
      render: (_, record) =>
        record.business_line?.name ? (
          <Text>{record.business_line.name}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "策略类型",
      key: "strategy_type",
      width: 120,
      ellipsis: true,
      render: (_, record) =>
        record.strategy_type?.name ? (
          <Text>{record.strategy_type.name}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "借币配置",
      key: "borrow_configs",
      width: 300,
      render: (_, record) => {
        const borrows = record.borrow_configs || [];
        if (borrows.length === 0) {
          return <Text type="secondary">暂无借币</Text>;
        }
        return (
          <div className="borrow-configs-cell">
            {borrows.map((borrow) => (
              <Tooltip key={borrow.id} title="点击编辑该笔借币">
                <Tag
                  className={`borrow-tag ${borrow.status}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => openBorrowModal(record, borrow)}
                >
                  <Coins size={12} />
                  {borrow.asset}: {borrow.borrow_amount}
                  {borrow.status !== "active" && (
                    <span className="borrow-status">
                      {borrow.status === "repaid" ? "已还" : "逾期"}
                    </span>
                  )}
                </Tag>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: "状态",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (isActive) => (
        <Tag className={`status-tag ${isActive ? "active" : "inactive"}`}>
          {isActive ? "启用" : "禁用"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "actions",
      width: 280,
      render: (_, record) => (
        <Space size="small" className="action-buttons">
          <Tooltip title="管理该账号的借币：新增 / 编辑 / 删除">
            <Button
              className="action-btn borrow-btn"
              icon={<Coins size={14} />}
              onClick={() => openBorrowListModal(record)}
              size="small"
            >
              借币管理
            </Button>
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              className="action-btn edit-btn"
              icon={<Edit size={14} />}
              onClick={() => openModal(record)}
              size="small"
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title={record.is_active ? "禁用" : "启用"}>
            <Popconfirm
              title={`确定要${record.is_active ? "禁用" : "启用"}此配置吗？`}
              onConfirm={() => handleToggleActive(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                className={`action-btn ${record.is_active ? "disable-btn" : "enable-btn"}`}
                size="small"
              >
                {record.is_active ? "禁用" : "启用"}
              </Button>
            </Popconfirm>
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定要删除此配置吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                className="action-btn delete-btn"
                icon={<Trash2 size={14} />}
                danger
                size="small"
              >
                删除
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 借币明细列：表格展开行与「借币管理」弹窗共用同一套定义
  const buildBorrowColumns = (config) => {
    return [
      { title: "资产", dataIndex: "asset", key: "asset", width: 80 },
      {
        title: "借币数量",
        dataIndex: "borrow_amount",
        key: "borrow_amount",
        width: 120,
      },
      {
        title: "借币日期",
        dataIndex: "borrow_date",
        key: "borrow_date",
        width: 120,
        render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "-"),
      },
      {
        title: "利率",
        dataIndex: "interest_rate",
        key: "interest_rate",
        width: 100,
        render: (rate) => (rate ? `${(rate * 100).toFixed(2)}%` : "-"),
      },
      {
        title: "到期日",
        dataIndex: "due_date",
        key: "due_date",
        width: 120,
        render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "-"),
      },
      {
        title: "状态",
        dataIndex: "status",
        key: "status",
        width: 80,
        render: (status) => {
          const statusMap = {
            active: { text: "生效中", color: "green" },
            repaid: { text: "已归还", color: "blue" },
            overdue: { text: "已逾期", color: "red" },
          };
          const { text, color } = statusMap[status] || { text: status, color: "default" };
          return <Tag color={color}>{text}</Tag>;
        },
      },
      { title: "备注", dataIndex: "remark", key: "remark", ellipsis: true },
      {
        title: "操作",
        key: "actions",
        width: 120,
        render: (_, borrow) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              onClick={() => openBorrowModal(config, borrow)}
            >
              编辑
            </Button>
            <Popconfirm
              title="确定删除此借币配置？"
              onConfirm={() => handleDeleteBorrow(config?.id, borrow.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];
  };

  const expandedRowRender = (record) => {
    const borrows = record.borrow_configs || [];
    if (borrows.length === 0) return null;

    return (
      <Table
        columns={buildBorrowColumns(record)}
        dataSource={borrows}
        rowKey="id"
        pagination={false}
        size="small"
        className="borrow-table"
      />
    );
  };

  return (
    <ErrorBoundary>
      <div className="account-config-page">
        {/* 页面头部 */}
        <div className="page-header">
          <div className="header-content">
            <Title level={2} className="page-title">
              <Settings className="title-icon" size={32} />
              账号配置
            </Title>
            <Text className="page-subtitle">
              一个账号对应一个交易所；可同时勾选现货与合约，系统将分别创建两条配置。新建时必须选择已启用的业务线与策略类型。
              {isUserAdmin
                ? " 管理员新建时需指定所属用户。"
                : " 您新建的账号将自动归属本人，无需选择所属用户。"}
            </Text>
          </div>
          {user && (
            <Button
              type="primary"
              size="large"
              icon={<Plus size={18} />}
              onClick={() => openModal()}
              className="create-button"
            >
              新增配置
            </Button>
          )}
        </div>

        {/* 配置列表 */}
        <Card className="config-list-card">
          <div className="filter-toolbar">
            <Space size="middle" wrap>
              <Input
                placeholder="账号 / 描述（模糊）"
                value={searchParams.keyword}
                onChange={(e) =>
                  setSearchParams({ ...searchParams, keyword: e.target.value })
                }
                onPressEnter={handleSearch}
                prefix={<Search size={16} />}
                style={{ width: 220 }}
                allowClear
              />
              <Select
                placeholder="交易所"
                value={searchParams.exchange || undefined}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, exchange: value || "" })
                }
                style={{ width: 150 }}
                allowClear
              >
                {exchanges.map((ex) => (
                  <Option key={ex.code || ex.name} value={ex.code || ex.name}>
                    {ex.name}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="交易类型"
                value={searchParams.trade_type || undefined}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, trade_type: value || "" })
                }
                style={{ width: 120 }}
                allowClear
              >
                <Option value="spot">现货</Option>
                <Option value="swap">合约</Option>
              </Select>
              <Select
                placeholder="启用状态"
                value={searchParams.is_active}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, is_active: value })
                }
                style={{ width: 120 }}
                allowClear
              >
                <Option value={true}>启用</Option>
                <Option value={false}>禁用</Option>
              </Select>
              <Select
                placeholder="业务线"
                value={searchParams.business_line_id}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, business_line_id: value })
                }
                style={{ width: 180 }}
                allowClear
                showSearch
                optionFilterProp="children"
                dropdownMatchSelectWidth={240}
              >
                <Option value={0}>未分配业务线</Option>
                {(activeBusinessLines || []).map((x) => (
                  <Option key={x.id} value={x.id}>
                    {x.code ? `${x.name}（${x.code}）` : x.name}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="策略类型"
                value={searchParams.strategy_type_id}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, strategy_type_id: value })
                }
                style={{ width: 180 }}
                allowClear
                showSearch
                optionFilterProp="children"
                dropdownMatchSelectWidth={240}
              >
                <Option value={0}>未分配策略类型</Option>
                {(activeStrategyTypes || []).map((x) => (
                  <Option key={x.id} value={x.id}>
                    {x.code ? `${x.name}（${x.code}）` : x.name}
                  </Option>
                ))}
              </Select>
              {isUserAdmin && (
                <Select
                  placeholder="所属用户"
                  value={searchParams.owner_user_id}
                  onChange={(value) =>
                    setSearchParams({ ...searchParams, owner_user_id: value })
                  }
                  style={{ width: 180 }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  dropdownMatchSelectWidth={240}
                >
                  <Option value={0}>未分配所属用户</Option>
                  {systemUsers.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {u.username}
                    </Option>
                  ))}
                </Select>
              )}
              <Select
                placeholder="借币状态"
                value={searchParams.borrow_status || undefined}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, borrow_status: value || "" })
                }
                style={{ width: 130 }}
                allowClear
              >
                <Option value="has">有借币</Option>
                <Option value="none">无借币</Option>
              </Select>
              <Button type="primary" onClick={handleSearch} icon={<Search size={16} />}>
                搜索
              </Button>
              <Button
                onClick={() => {
                  setSearchParams({
                    keyword: "",
                    exchange: "",
                    trade_type: "",
                    is_active: undefined,
                    owner_user_id: undefined,
                    business_line_id: undefined,
                    strategy_type_id: undefined,
                    borrow_status: "",
                  });
                  setPagination({ ...pagination, current: 1 });
                }}
              >
                重置
              </Button>
              <Button icon={<RefreshCw size={16} />} onClick={loadConfigs}>
                刷新
              </Button>
              <Button
                icon={<Edit size={16} />}
                onClick={openBatchModal}
                disabled={selectedRowKeys.length === 0}
              >
                批量更新{selectedRowKeys.length > 0 ? `（${selectedRowKeys.length}）` : ""}
              </Button>
              <Tooltip title="导出名下全部账号配置（含借币明细）；有筛选条件时按筛选结果导出">
                <Button
                  icon={<Download size={16} />}
                  onClick={handleExport}
                  loading={exporting}
                >
                  导出数据
                </Button>
              </Tooltip>
            </Space>
          </div>
          <Table
            columns={columns}
            dataSource={configs}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableChange}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) =>
                record.borrow_configs && record.borrow_configs.length > 0,
            }}
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              preserveSelectedRowKeys: true,
            }}
            scroll={{ x: "max-content" }}
            className="configs-table"
          />
        </Card>

        {/* 账号配置 Modal */}
        <Modal
          title={
            <div className="modal-title">
              <Settings size={20} />
              <span>{currentConfig ? "编辑配置" : "新增配置"}</span>
            </div>
          }
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            form.resetFields();
            setCurrentConfig(null);
          }}
          footer={null}
          className="config-modal"
          width={560}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ trade_types: ["spot"], is_active: true }}
            className="config-form"
          >
            <Form.Item
              label="账号名称"
              name="account"
              rules={[{ required: true, message: "请输入账号名称" }]}
            >
              <Input placeholder="输入账号名称" size="large" disabled={!!currentConfig} />
            </Form.Item>

            <Form.Item
              label="交易所"
              name="exchange"
              rules={[{ required: true, message: "请选择交易所" }]}
            >
              <Select placeholder="选择交易所" size="large" disabled={!!currentConfig}>
                {exchanges.map((ex) => (
                  <Option key={ex.code || ex.name} value={ex.code || ex.name}>
                    {ex.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {currentConfig ? (
              <Form.Item label="交易类型">
                <Tag color={currentConfig.trade_type === "spot" ? "green" : "blue"}>
                  {currentConfig.trade_type === "spot" ? "现货" : "合约"}
                </Tag>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  类型与账号、交易所绑定；需新增类型请使用「新增配置」并勾选对应类型。
                </Text>
              </Form.Item>
            ) : (
              <Form.Item
                label="交易类型"
                name="trade_types"
                rules={[
                  {
                    validator: (_, value) =>
                      value && value.length > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error("请至少选择现货或合约中的一项")),
                  },
                ]}
              >
                <Checkbox.Group>
                  <Space>
                    <Checkbox value="spot">现货</Checkbox>
                    <Checkbox value="swap">合约</Checkbox>
                  </Space>
                </Checkbox.Group>
              </Form.Item>
            )}

            {!currentConfig &&
              (activeBusinessLines.length === 0 || activeStrategyTypes.length === 0) && (
                <Alert
                  type="warning"
                  showIcon
                  message="缺少主数据"
                  description="请先在侧栏「业务线」「策略类型」页面创建并启用至少一条记录，否则无法提交新建。"
                  style={{ marginBottom: 16 }}
                />
              )}

            <Form.Item
              label="业务线"
              name="business_line_id"
              rules={[{ required: true, message: "请选择业务线" }]}
            >
              <Select
                placeholder="选择业务线"
                size="large"
                showSearch
                optionFilterProp="children"
              >
                {businessLineOptions.map((x) => (
                  <Option key={x.id} value={x.id}>
                    {x.code ? `${x.name}（${x.code}）` : x.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="策略类型"
              name="strategy_type_id"
              rules={[{ required: true, message: "请选择策略类型" }]}
            >
              <Select
                placeholder="选择策略类型"
                size="large"
                showSearch
                optionFilterProp="children"
              >
                {strategyTypeOptions.map((x) => (
                  <Option key={x.id} value={x.id}>
                    {x.code ? `${x.name}（${x.code}）` : x.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="描述" name="description">
              <TextArea rows={3} placeholder="配置描述（可选）" />
            </Form.Item>

            {!isUserAdmin && !currentConfig && (
              <Alert
                type="info"
                showIcon
                message="归属说明"
                description={`新建账号将自动归属为当前登录用户「${user?.username ?? ""}」，不可指定为他人。`}
                style={{ marginBottom: 16 }}
              />
            )}

            {isUserAdmin && (
              <Form.Item
                label="所属用户"
                name="owner_user_id"
                rules={[{ required: true, message: "请选择所属用户" }]}
                tooltip="该账号由哪位系统用户维护与归属统计"
              >
                <Select
                  placeholder="选择系统用户"
                  size="large"
                  allowClear={!!currentConfig}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.label ?? option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {systemUsers.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {u.username}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item label="状态" name="is_active" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>

            <Form.Item className="form-actions">
              <Space>
                <Button
                  onClick={() => {
                    setShowModal(false);
                    form.resetFields();
                    setCurrentConfig(null);
                  }}
                >
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  {currentConfig ? "更新" : "创建"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* 借币管理 Modal：借贷信息的统一入口，列出该账号全部借币并支持增删改 */}
        <Modal
          title={
            <div className="modal-title">
              <Coins size={20} />
              <span>
                借币管理
                {currentConfig
                  ? ` · ${currentConfig.account}（${currentConfig.trade_type === "spot" ? "现货" : "合约"}）`
                  : ""}
              </span>
            </div>
          }
          open={showBorrowListModal}
          onCancel={() => setShowBorrowListModal(false)}
          footer={null}
          width={980}
          destroyOnClose
        >
          <div style={{ marginBottom: 12, textAlign: "right" }}>
            <Button
              type="primary"
              icon={<Plus size={14} />}
              onClick={() => openBorrowModal(currentConfig)}
            >
              添加借币
            </Button>
          </div>
          <Table
            columns={buildBorrowColumns(currentConfig)}
            dataSource={currentConfigBorrows}
            rowKey="id"
            pagination={false}
            size="small"
            className="borrow-table"
            locale={{ emptyText: "暂无借币，点击右上角「添加借币」新增" }}
          />
        </Modal>

        {/* 借币配置 Modal */}
        <Modal
          title={
            <div className="modal-title">
              <Coins size={20} />
              <span>{currentBorrow ? "编辑借币" : "添加借币"}</span>
            </div>
          }
          open={showBorrowModal}
          onCancel={() => {
            setShowBorrowModal(false);
            borrowForm.resetFields();
            setCurrentBorrow(null);
          }}
          footer={null}
          className="borrow-modal"
          width={520}
        >
          <Form
            form={borrowForm}
            layout="vertical"
            onFinish={handleBorrowSubmit}
            initialValues={{ status: "active" }}
            className="borrow-form"
          >
            <Form.Item
              label="资产"
              name="asset"
              rules={[{ required: true, message: "请输入资产名称" }]}
            >
              <Input placeholder="例如：USDT、BTC、ETH" size="large" />
            </Form.Item>

            <Form.Item
              label="借币数量"
              name="borrow_amount"
              rules={[{ required: true, message: "请输入借币数量" }]}
            >
              <InputNumber
                placeholder="借币数量"
                size="large"
                style={{ width: "100%" }}
                min={0}
                precision={8}
              />
            </Form.Item>

            <Form.Item label="借币日期" name="borrow_date">
              <DatePicker style={{ width: "100%" }} size="large" />
            </Form.Item>

            <Form.Item label="利率" name="interest_rate">
              <InputNumber
                placeholder="年化利率（如 0.05 表示 5%）"
                size="large"
                style={{ width: "100%" }}
                min={0}
                max={1}
                step={0.01}
                precision={4}
              />
            </Form.Item>

            <Form.Item label="到期日" name="due_date">
              <DatePicker style={{ width: "100%" }} size="large" />
            </Form.Item>

            <Form.Item label="状态" name="status">
              <Select size="large">
                <Option value="active">生效中</Option>
                <Option value="repaid">已归还</Option>
                <Option value="overdue">已逾期</Option>
              </Select>
            </Form.Item>

            <Form.Item label="备注" name="remark">
              <TextArea rows={2} placeholder="备注信息（可选）" />
            </Form.Item>

            <Form.Item className="form-actions">
              <Space>
                <Button
                  onClick={() => {
                    setShowBorrowModal(false);
                    borrowForm.resetFields();
                    setCurrentBorrow(null);
                  }}
                >
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  {currentBorrow ? "更新" : "添加"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* 批量更新 Modal */}
        <Modal
          title={
            <div className="modal-title">
              <Edit size={20} />
              <span>批量更新账号配置</span>
            </div>
          }
          open={showBatchModal}
          onCancel={() => {
            setShowBatchModal(false);
            batchForm.resetFields();
          }}
          footer={null}
          width={560}
          destroyOnClose
        >
          <Alert
            type="info"
            showIcon
            message={`已选中 ${selectedRowKeys.length} 条账号配置`}
            description="只填写需要变更的字段，未填写的字段保持原值不变。所属用户 / 业务线 / 策略类型选「清空」可将字段清除。"
            style={{ marginBottom: 16 }}
          />

          <Form
            form={batchForm}
            layout="vertical"
            onFinish={handleBatchSubmit}
          >
            <Form.Item label="启用状态" name="is_active">
              <Select placeholder="不修改" allowClear>
                <Option value={true}>启用</Option>
                <Option value={false}>禁用</Option>
              </Select>
            </Form.Item>

            <Form.Item label="业务线" name="business_line_id">
              <Select
                placeholder="不修改"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                <Option value={0}>清空（不归属任何业务线）</Option>
                {(activeBusinessLines || []).map((x) => (
                  <Option key={x.id} value={x.id}>
                    {x.code ? `${x.name}（${x.code}）` : x.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="策略类型" name="strategy_type_id">
              <Select
                placeholder="不修改"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                <Option value={0}>清空（不归属任何策略类型）</Option>
                {(activeStrategyTypes || []).map((x) => (
                  <Option key={x.id} value={x.id}>
                    {x.code ? `${x.name}（${x.code}）` : x.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {isUserAdmin && (
              <Form.Item
                label="所属用户"
                name="owner_user_id"
                tooltip="仅管理员可批量调整所属用户"
              >
                <Select
                  placeholder="不修改"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value={0}>清空（不归属任何用户）</Option>
                  {systemUsers.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {u.username}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Divider style={{ margin: "12px 0" }} />

            <Form.Item className="form-actions" style={{ marginBottom: 0 }}>
              <Space>
                <Button
                  onClick={() => {
                    setShowBatchModal(false);
                    batchForm.resetFields();
                  }}
                  disabled={batchSubmitting}
                >
                  取消
                </Button>
                <Button type="primary" htmlType="submit" loading={batchSubmitting}>
                  提交批量更新
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default AccountConfigList;
